/**
 * Soglia di incentivabilità e zona della configurazione — Regole Operative CACER
 * (Allegato 1, vers. 16 luglio 2025), solo in energia.
 *
 * SOGLIA (§2.2.2.1.3):
 *   55% dell'energia immessa → impianti con la sola tariffa premio
 *   45% dell'energia immessa → impianti con contributo in conto capitale (es. PNRR)
 *   L'energia condivisa incentivabile oltre la soglia è eccedentaria: la sua quota
 *   va obbligatoriamente ai consumatori non-impresa o a finalità sociali.
 *
 * Il GSE verifica la soglia a conguaglio, su base annuale: il valore di un mese è
 * indicativo, non il definitivo.
 */

import {
	SOGLIA_INCENTIVABILITA_PNRR,
	SOGLIA_INCENTIVABILITA_STANDARD,
} from '../constants/gse-tariffs';

/** Macro-zone geografiche GSE (RO CACER §2.2.2.1.2, note 6–7). */
export type MacroZonaGSE = 'nord' | 'centro' | 'sud';

/** Regioni del Nord e del Centro (RO CACER §2.2.2.1.2, note 6–7). */
const REGIONI_NORD = new Set([
	'emilia-romagna',
	'friuli-venezia giulia',
	'liguria',
	'lombardia',
	'piemonte',
	'trentino-alto adige',
	"valle d'aosta",
	'veneto',
]);
const REGIONI_CENTRO = new Set(['lazio', 'marche', 'toscana', 'umbria', 'abruzzo']);
const REGIONI_SUD = new Set([
	'basilicata',
	'calabria',
	'campania',
	'molise',
	'puglia',
	'sardegna',
	'sicilia',
]);

/** Nome di regione in forma canonica: minuscolo, trattini uniformi, senza il nome bilingue. */
function normalizzaRegione(regione: string | null | undefined): string {
	return (regione ?? '')
		.toLowerCase()
		.split('/')[0]!
		.replace(/[–—]/g, '-')
		.replace(/\s*-\s*/g, '-')
		.replace(/\s+/g, ' ')
		.trim()
		.replace(/friuli[ -]venezia[ -]giulia/, 'friuli-venezia giulia')
		.replace(/^valle d.aosta$/, "valle d'aosta");
}

/**
 * Macro-zona dalla regione dell'impianto (INB-326). Tollera le varianti di
 * scrittura (trattini, «Trentino-Alto Adige/Südtirol», «Friuli Venezia
 * Giulia»). Regione assente o non riconosciuta: `null`.
 */
export function macroZonaDaRegione(regione: string | null | undefined): MacroZonaGSE | null {
	const norm = normalizzaRegione(regione);
	if (!norm) return null;
	if (REGIONI_NORD.has(norm)) return 'nord';
	if (REGIONI_CENTRO.has(norm)) return 'centro';
	return REGIONI_SUD.has(norm) ? 'sud' : null;
}

/**
 * Zona da mostrare per un'organizzazione (INB-372). La zona vera è della
 * configurazione: la CER che le raggruppa ha una colonna `zona_geografica` che
 * nessuno imposta e che vale `sud` di default. Con configurazioni, quindi, conta
 * la loro zona se è una sola; se sono in zone diverse non c'è una zona della
 * CER, e si restituisce `null` invece di un valore inventato. Senza
 * configurazioni l'organizzazione è una configurazione, e vale la sua.
 */
export function zonaOrganizzazione(
	propria: MacroZonaGSE | null | undefined,
	configurazioni: readonly (MacroZonaGSE | null | undefined)[],
): MacroZonaGSE | null {
	if (configurazioni.length === 0) return propria ?? null;
	const zone = new Set(configurazioni.filter((z): z is MacroZonaGSE => !!z));
	return zone.size === 1 ? ([...zone][0] ?? null) : null;
}

/**
 * Energia eccedentaria di un periodo: l'energia condivisa incentivabile oltre la
 * soglia, `max[0; E_ACI − soglia × E_immessa]` (RO §2.2.2.1.3).
 *
 * - `E_ACI` è l'energia condivisa **incentivabile** (al netto della quota
 *   preesistente oltre il 30%), non la condivisa totale;
 * - con `quotaImmessaPnrr` la soglia si applica per insieme di impianti
 *   (INB-324): 55% sulla parte senza contributo, 45% su quella con. L'energia
 *   condivisa si divide fra i due insiemi in proporzione all'immessa, che è
 *   anche come il GSE la attribuisce ora per ora. Senza, vale `hasPnrrGrant`
 *   per tutta la configurazione.
 */
export function eccedenzaPeriodo(params: {
	/** E_ACI: energia condivisa incentivabile del periodo, kWh. */
	energiaIncentivabileKwh: number;
	/** E_immessa: energia immessa dagli impianti della configurazione, kWh. */
	energiaImmessaKwh: number;
	/** Almeno un impianto ha il contributo in conto capitale: soglia 45% invece di 55%. */
	hasPnrrGrant: boolean;
	/** Quota (0–1) dell'energia immessa dagli impianti con contributo in conto capitale. */
	quotaImmessaPnrr?: number | null;
}): { eccedenzaKwh: number } {
	const incentivabile = Math.max(0, params.energiaIncentivabileKwh);
	const immessa = Math.max(0, params.energiaImmessaKwh);
	const oltre = (quota: number, soglia: number) =>
		immessa * quota > 0 ? Math.max(0, incentivabile * quota - immessa * quota * soglia) : 0;
	if (params.quotaImmessaPnrr != null && Number.isFinite(params.quotaImmessaPnrr)) {
		const q = Math.min(1, Math.max(0, params.quotaImmessaPnrr));
		return {
			eccedenzaKwh:
				oltre(1 - q, SOGLIA_INCENTIVABILITA_STANDARD) + oltre(q, SOGLIA_INCENTIVABILITA_PNRR),
		};
	}
	return {
		eccedenzaKwh: oltre(
			1,
			params.hasPnrrGrant ? SOGLIA_INCENTIVABILITA_PNRR : SOGLIA_INCENTIVABILITA_STANDARD,
		),
	};
}

/**
 * Quota (0–1) dell'energia immessa che viene dagli impianti con contributo in
 * conto capitale, per {@link eccedenzaPeriodo} (INB-324). `null` senza energia
 * nel periodo: la soglia torna a valere per tutta la configurazione.
 */
export function quotaImmessaPnrr(
	plants: readonly { kwh: number; grantPct: number }[],
): number | null {
	const tot = plants.reduce((s, p) => s + (Number(p.kwh) || 0), 0);
	if (tot <= 0) return null;
	const pnrr = plants.filter((p) => p.grantPct > 0).reduce((s, p) => s + (Number(p.kwh) || 0), 0);
	return pnrr / tot;
}
